import { AnyAction, TaskAbortError } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { LOCATION_CHANGE, LocationChangeAction, push } from 'connected-react-router';
import _get from 'lodash/get';
import _omit from 'lodash/omit';
import { matchPath } from 'react-router';
import { Config } from '../../config';
import { r } from '../../routes';
import { startAppListening } from '../../state/middlewares/listener';
import { logError, urlParser } from '../../utils/basics';
import { trackEvent } from '../../utils/eventTracking';
import { ROLES } from '../../utils/global';
import { translate } from '../../utils/localization';
import { coreClient, http, routePlannerClient } from '../../utils/request';
import { getLoggedInUser, isRegistered, isSocialRegistration, actions as userActions } from './ducks';
import { afterLoginRedirect } from './hooks/useLogin';
import { UserActionTypes, UserErrorTitles } from './typings';
import { retrieveCurrentUser } from './utils/retrieveCurrentUser';

/**
 * A replaced (cancelled) listener stops where it is, without running its error handling (as a cancelled saga did).
 */
function rethrowIfCancelled(e: unknown): void {
  if (e instanceof TaskAbortError) throw e;
}

function startSocialAuth(action): void {
  const URL = urlParser();
  const uuid = URL.get('transport_request');
  const referer = uuid && uuid !== '' ? `&transport_request=${uuid}` : '';
  window.location.assign(`${Config.API_URL}/user/login?social=${action.service}${referer}`);
}

function completeRegistration(details, id): Promise<AxiosResponse> {
  details['username'] = details.email;
  details['roles'] = [ROLES.role_customer, ROLES.role_user];
  return http()
    .put(`${id}/complete_social_registration`, details, { withCredentials: true })
    .then(resp => resp);
}

async function createIndividualAccount(details): Promise<void> {
  const l = new URL(window.location.href);
  details.account = {
    account_type: 'individual',
    name: details.first_name + ' ' + details.last_name,
    phone: details.phone,
  };
  details.username = details.email;
  details.roles = [ROLES.role_customer, ROLES.role_user];
  details.trId = l.searchParams.get('transport_request') || undefined;
  await coreClient.users.create(details);
}

async function createBusinessAccount(details): Promise<void> {
  const l = new URL(window.location.href);
  details.account.account_type = 'business';
  details.account.phone = details.phone;
  details.username = details.email;
  details.roles = [ROLES.role_customer, ROLES.role_user];
  details.trId = l.searchParams.get('transport_request') || undefined;
  await coreClient.users.create(details);
}

function createAddress(addr): Promise<AxiosResponse> {
  return http()
    .post('addresses', addr, { withCredentials: true })
    .then(resp => resp);
}

function updateAccount(id, account): Promise<AxiosResponse> {
  return http()
    .put(id, account, { withCredentials: true })
    .then(resp => resp);
}

/**
 * Registers the user side effects (same behavior as the former userSagas: userAccountSaga and socialAuthSaga).
 */
export const startUserListeners = (): void => {
  /**
   * Stores the country found by IP; the latest lookup wins (same behavior as the former checkWhatIsMyCountry saga).
   */
  startAppListening({
    type: UserActionTypes.RETRIEVE_CURRENT_COUNTRY,
    effect: async (_action, api) => {
      api.cancelActiveListeners();
      try {
        const countryCode = await api.pause(routePlannerClient.geo.whatIsMyCountry());
        api.dispatch(userActions.setWhatIsMyCountry(countryCode));
      } catch (err) {
        rethrowIfCancelled(err);
        logError('cannot fetch the geo location based on IP');
      }
    },
  });

  /**
   * On the social auth callback page, loads the user and sends them on; any navigation replaces a pending load
   * (same behavior as the former verifyLoggedInUser saga).
   */
  startAppListening({
    type: LOCATION_CHANGE,
    effect: async (action, api) => {
      api.cancelActiveListeners();
      if (matchPath((action as LocationChangeAction).payload.location.pathname, { path: r.user.loginCheck() })) {
        try {
          const registered = isRegistered(api.getState());
          const redirectUrl = registered ? r.user.dashboard() : r.user.register();
          const user = await api.pause(coreClient.users.retrieveCurrentUser());
          api.dispatch(userActions.socialAuthSuccess(user));
          api.dispatch(push(redirectUrl));
        } catch (e) {
          rethrowIfCancelled(e);
          logError(e);
          api.dispatch(userActions.socialAuthFailed('Social auth failed'));
        }
      }
    },
  });

  /**
   * On the social auth error page, sends the user to registration after 5 seconds unless they navigate away first
   * (same behavior as the former failedSocialAuth saga).
   */
  startAppListening({
    type: LOCATION_CHANGE,
    effect: async (action, api) => {
      api.cancelActiveListeners();
      if (
        matchPath((action as LocationChangeAction).payload.location.pathname, {
          path: r.user.loginError(),
          exact: true,
        })
      ) {
        await api.delay(5000);
        api.dispatch(push(r.user.register()));
      }
    },
  });

  /**
   * Leaves for the social login of core (same behavior as the former startSocialAuth saga).
   */
  startAppListening({
    type: UserActionTypes.SOCIAL_AUTH_START,
    effect: action => {
      startSocialAuth(action);
    },
  });

  /**
   * Registers the user, completing a social registration when there is one; the latest submit wins
   * (same behavior as the former registerUser saga).
   */
  startAppListening({
    type: UserActionTypes.REGISTER_USER_START,
    effect: async (anyAction, api) => {
      api.cancelActiveListeners();
      const action = anyAction as AnyAction;
      let formPayload = action.payload.user;
      if (_get(action.payload.user, 'account', false)) {
        formPayload = _omit(action.payload.user, 'account');
      }
      try {
        api.dispatch(userActions.registerErrorReset());
        const isExistUsername = await api.pause(coreClient.users.retrieveUserExists(action.payload.user.email));
        if (isExistUsername === UserErrorTitles.USERNAME_EXIST) {
          api.dispatch(userActions.registerFailed(translate('authentication.register.error.username_taken')));
          return;
        }
      } catch (e) {
        rethrowIfCancelled(e);
        // If the username doesn't exist it returns 404
        logError(e);
      }

      try {
        const isSocialReg = isSocialRegistration(api.getState());
        if (isSocialReg) {
          const user = getLoggedInUser(api.getState());
          // social logged in user registration scenario
          if (user.isBusiness) {
            const account = action.payload.user.account;
            account.phone = action.payload.user.phone;
            await api.pause(
              Promise.all([
                updateAccount(user.details?.account['@id'], account),
                createAddress(account),
                completeRegistration(formPayload, user.id),
              ])
            );
            api.dispatch(userActions.registerSuccess(action.payload.user));
          } else {
            await api.pause(completeRegistration(formPayload, user.id));
            api.dispatch(userActions.registerSuccess(action.payload.user));
          }
          trackEvent({
            eventCategory: 'user_registered',
            eventAction: 'via_social',
            eventLabel: user.isBusiness ? 'business' : 'private',
          });
        } else {
          // without social account registration scenario
          const isBusiness = action.payload.user.type === 'business';
          if (isBusiness) {
            await api.pause(createBusinessAccount(action.payload.user));
            api.dispatch(userActions.registerSuccess(action.payload.user));
          } else {
            // test failure
            await api.pause(createIndividualAccount(action.payload.user));
            api.dispatch(userActions.registerSuccess(action.payload.user));
          }
          trackEvent({
            eventCategory: 'user_registered',
            eventAction: 'not_via_social',
            eventLabel: isBusiness ? 'business' : 'private',
          });
        }
      } catch (err) {
        rethrowIfCancelled(err);
        logError(err);
        api.dispatch(userActions.registerFailed('User registration failed'));
      }
    },
  });

  /**
   * Logs the newly registered user in and redirects them; the latest registration wins
   * (same behavior as the former afterRegister saga).
   */
  startAppListening({
    type: UserActionTypes.REGISTER_USER_SUCCESS,
    effect: async (anyAction, api) => {
      api.cancelActiveListeners();
      const action = anyAction as AnyAction;
      const l = new URL(window.location.href);
      const email = action.payload.email;
      const password = action.payload.new_password;
      if (email && password) {
        try {
          await api.pause(
            coreClient.users.login({
              email,
              password,
              trId: l.searchParams.get('transport_request') || undefined,
            })
          );
          await api.pause(retrieveCurrentUser());
          afterLoginRedirect();
        } catch (e) {
          rethrowIfCancelled(e);
          logError(e);
        }
      }
    },
  });
};
